// store/data/slice.ts

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DataState } from '@/types/data';
import { getStudyData, getCompanyData } from './thunks';

const initialState: DataState = {
  // Core data
  studyData: [],
  companyData: [],

  // Loading states
  isLoadingStudyData: false,
  isLoadingCompanyData: false,

  // Error states
  studyDataError: null,
  companyDataError: null,

  // Filters (for caching/state management)
  lastStudyDataFilters: null,
  lastCompanyDataFilters: null,

  // Metadata
  studyDataCount: 0,
  companyDataCount: 0,
  lastUpdated: null,
};

export const dataSlice = createSlice({
  name: 'data',
  initialState,
  reducers: {
    // ------------------------------------ CLEAR ACTIONS ------------------------------------

    clearStudyDataError: state => {
      state.studyDataError = null;
    },

    clearCompanyDataError: state => {
      state.companyDataError = null;
    },

    clearAllErrors: state => {
      state.studyDataError = null;
      state.companyDataError = null;
    },

    // ------------------------------------ FILTER ACTIONS ------------------------------------

    setStudyDataFilters: (state, action: PayloadAction<any>) => {
      state.lastStudyDataFilters = action.payload;
    },

    setCompanyDataFilters: (state, action: PayloadAction<any>) => {
      state.lastCompanyDataFilters = action.payload;
    },

    clearStudyDataFilters: state => {
      state.lastStudyDataFilters = null;
    },

    clearCompanyDataFilters: state => {
      state.lastCompanyDataFilters = null;
    },

    clearAllFilters: state => {
      state.lastStudyDataFilters = null;
      state.lastCompanyDataFilters = null;
    },

    // ------------------------------------ RESET ACTIONS ------------------------------------

    resetStudyData: state => {
      state.studyData = [];
      state.studyDataCount = 0;
      state.lastStudyDataFilters = null;
      state.studyDataError = null;
    },

    resetCompanyData: state => {
      state.companyData = [];
      state.companyDataCount = 0;
      state.lastCompanyDataFilters = null;
      state.companyDataError = null;
    },

    resetState: () => initialState,
  },
  extraReducers: builder => {
    builder
      // --------------------------------------- STUDY DATA ---------------------------------------

      // Get Study Data
      .addCase(getStudyData.pending, state => {
        state.isLoadingStudyData = true;
        state.studyDataError = null;
      })
      .addCase(getStudyData.fulfilled, (state, action) => {
        state.isLoadingStudyData = false;
        state.studyDataError = null;
        state.studyData = action.payload;
        state.studyDataCount = action.payload.length;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(getStudyData.rejected, (state, action) => {
        state.isLoadingStudyData = false;
        state.studyDataError = action.payload as string;
      })

      // --------------------------------------- COMPANY DATA ---------------------------------------

      // Get Company Data
      .addCase(getCompanyData.pending, state => {
        state.isLoadingCompanyData = true;
        state.companyDataError = null;
      })
      .addCase(getCompanyData.fulfilled, (state, action) => {
        state.isLoadingCompanyData = false;
        state.companyDataError = null;
        state.companyData = action.payload;
        state.companyDataCount = action.payload.length;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(getCompanyData.rejected, (state, action) => {
        state.isLoadingCompanyData = false;
        state.companyDataError = action.payload as string;
      });
  },
});

export const {
  clearStudyDataError,
  clearCompanyDataError,
  clearAllErrors,
  setStudyDataFilters,
  setCompanyDataFilters,
  clearStudyDataFilters,
  clearCompanyDataFilters,
  clearAllFilters,
  resetStudyData,
  resetCompanyData,
  resetState,
} = dataSlice.actions;

export const dataReducer = dataSlice.reducer;
